/**
 * ApiWorkbenchPanel — the Postman-style surface for API/Security testing.
 *
 * You declare (or import from cURL/OpenAPI) the requests you want tested, add
 * assertions, and toggle which security attacks to run per request. "Send"
 * fires a single request live (like Postman) so you can check it works;
 * "Run tests in agent" hands the whole curated collection to the agent, which
 * generates functional tests + the security variants you enabled.
 *
 * This is the API/security face of the editable plan — the collection IS the
 * plan the agent runs.
 */

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box, Button, Chip, CircularProgress, Dialog, DialogContent, DialogTitle,
  Divider, IconButton, MenuItem, Select, Tab, Tabs, TextField, Tooltip,
  Typography, Alert, FormControlLabel, Checkbox,
} from '@mui/material';
import {
  Api as ApiIcon, Add as AddIcon, Delete as DeleteIcon, Send as SendIcon,
  Download as ImportIcon, Save as SaveIcon, PlayArrow as RunIcon,
} from '@mui/icons-material';
import {
  getApiWorkbench, saveApiWorkbench, importApiWorkbench, sendApiRequest,
  ApiCollection, ApiRequest, ApiKV,
} from '../services/project.service';

interface Props {
  projectId?: string | number | null;
  onRunInAgent?: () => void;
}

const METHODS = ['GET', 'POST', 'PUT', 'PATCH', 'DELETE', 'HEAD', 'OPTIONS'];
const METHOD_COLOR: Record<string, string> = {
  GET: '#2e7d32', POST: '#1565c0', PUT: '#e65100', PATCH: '#6a1b9a', DELETE: '#c62828',
};
const ATTACKS: { key: string; label: string }[] = [
  { key: 'idor', label: 'IDOR (swap object id)' },
  { key: 'authz', label: 'Authz bypass (drop/swap token)' },
  { key: 'sqli', label: 'SQL injection' },
  { key: 'xss', label: 'XSS' },
  { key: 'csrf', label: 'Missing/forged CSRF' },
  { key: 'fuzz', label: 'Type/bounds fuzz' },
];

const blankRequest = (): ApiRequest => ({
  name: 'New request', method: 'GET', url: '', headers: [], query: [],
  body: '', body_type: 'raw', auth: { type: 'inherit' },
  assertions: { status: 200, contains: [] }, security: { attacks: [] },
});

// Small key/value rows editor
const KVEditor: React.FC<{ rows: ApiKV[]; onChange: (r: ApiKV[]) => void }> = ({ rows, onChange }) => (
  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
    {rows.map((row, i) => (
      <Box key={i} sx={{ display: 'flex', gap: 0.5 }}>
        <TextField size="small" placeholder="key" value={row.key} sx={{ flex: 1 }}
          onChange={(e) => onChange(rows.map((r, idx) => idx === i ? { ...r, key: e.target.value } : r))} />
        <TextField size="small" placeholder="value" value={row.value} sx={{ flex: 2 }}
          onChange={(e) => onChange(rows.map((r, idx) => idx === i ? { ...r, value: e.target.value } : r))} />
        <IconButton size="small" onClick={() => onChange(rows.filter((_, idx) => idx !== i))}>
          <DeleteIcon fontSize="small" />
        </IconButton>
      </Box>
    ))}
    <Button size="small" startIcon={<AddIcon />} onClick={() => onChange([...rows, { key: '', value: '' }])}
      sx={{ textTransform: 'none', alignSelf: 'flex-start' }}>Add</Button>
  </Box>
);

const ApiWorkbenchPanel: React.FC<Props> = ({ projectId, onRunInAgent }) => {
  const [open, setOpen] = useState(false);
  const [col, setCol] = useState<ApiCollection>({ requests: [], variables: {} });
  const [sel, setSel] = useState(0);
  const [tab, setTab] = useState(0);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [savedMsg, setSavedMsg] = useState(false);

  const [sending, setSending] = useState(false);
  const [response, setResponse] = useState<any>(null);

  const [importOpen, setImportOpen] = useState(false);
  const [importKind, setImportKind] = useState<'curl' | 'openapi'>('curl');
  const [curlText, setCurlText] = useState('');
  const [openapiUrl, setOpenapiUrl] = useState('');
  const [varsText, setVarsText] = useState('');

  const load = useCallback(() => {
    if (!projectId) return;
    setLoading(true); setError(null);
    getApiWorkbench(String(projectId))
      .then((c) => {
        setCol({ requests: c.requests || [], variables: c.variables || {} });
        setVarsText(Object.entries(c.variables || {}).map(([k, v]) => `${k}=${v}`).join('\n'));
        setSel(0);
      })
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false));
  }, [projectId]);

  useEffect(() => { if (open) load(); }, [open, load]);

  const current: ApiRequest | undefined = col.requests[sel];
  const updateCurrent = (patch: Partial<ApiRequest>) =>
    setCol((c) => ({ ...c, requests: c.requests.map((r, i) => (i === sel ? { ...r, ...patch } : r)) }));

  const parseVars = (): Record<string, string> => {
    const out: Record<string, string> = {};
    varsText.split('\n').forEach((line) => {
      const idx = line.indexOf('=');
      if (idx > 0) out[line.slice(0, idx).trim()] = line.slice(idx + 1).trim();
    });
    return out;
  };

  const addRequest = () => setCol((c) => { const rs = [...c.requests, blankRequest()]; setSel(rs.length - 1); return { ...c, requests: rs }; });
  const removeRequest = (i: number) => setCol((c) => {
    const rs = c.requests.filter((_, idx) => idx !== i);
    setSel(Math.max(0, Math.min(sel, rs.length - 1)));
    return { ...c, requests: rs };
  });

  const handleSave = async () => {
    if (!projectId) return;
    setSaving(true); setError(null); setSavedMsg(false);
    try {
      const saved = await saveApiWorkbench(String(projectId), { requests: col.requests, variables: parseVars() });
      setCol({ requests: saved.requests || [], variables: saved.variables || {} });
      setSavedMsg(true);
    } catch (e: any) { setError(e.message); } finally { setSaving(false); }
  };

  const handleSend = async () => {
    if (!projectId || !current) return;
    setSending(true); setError(null); setResponse(null);
    try {
      setResponse(await sendApiRequest(String(projectId), current, parseVars()));
    } catch (e: any) { setError(e.message); } finally { setSending(false); }
  };

  const handleImport = async () => {
    if (!projectId) return;
    setError(null);
    try {
      const payload = importKind === 'curl'
        ? { kind: 'curl' as const, curl: curlText }
        : { kind: 'openapi' as const, openapi_url: openapiUrl };
      const { requests } = await importApiWorkbench(String(projectId), payload);
      setCol((c) => ({ ...c, requests: [...c.requests, ...requests] }));
      setImportOpen(false); setCurlText(''); setOpenapiUrl('');
    } catch (e: any) { setError(e.message); }
  };

  const toggleAttack = (key: string) => {
    const cur = current?.security?.attacks || [];
    updateCurrent({ security: { attacks: cur.includes(key) ? cur.filter((a) => a !== key) : [...cur, key] } });
  };

  const respStatus = response?.status_code ?? response?.status;

  if (!projectId) return null;

  return (
    <>
      <Tooltip title="API / Security Workbench">
        <Button size="small" variant="outlined" startIcon={<ApiIcon />}
          onClick={() => setOpen(true)} sx={{ ml: 1, textTransform: 'none' }}>
          API Workbench
        </Button>
      </Tooltip>

      <Dialog open={open} onClose={() => setOpen(false)} maxWidth="lg" fullWidth
        PaperProps={{ sx: { height: '86vh' } }}>
        <DialogTitle sx={{ pb: 0.5 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <ApiIcon fontSize="small" />
            <span>API / Security Workbench</span>
          </Box>
          <Typography variant="caption" color="text.secondary">
            Declare the requests to test. Send checks one live; Run generates
            functional + security tests from the whole collection.
          </Typography>
        </DialogTitle>
        <DialogContent sx={{ display: 'flex', gap: 1.5, overflow: 'hidden' }}>
          {error && <Alert severity="error" sx={{ position: 'absolute', top: 8, right: 24, zIndex: 2 }}>{error}</Alert>}

          {/* Left: request list */}
          <Box sx={{ width: 240, borderRight: '1px solid', borderColor: 'divider', pr: 1, display: 'flex', flexDirection: 'column' }}>
            <Box sx={{ display: 'flex', gap: 0.5, mb: 1 }}>
              <Button size="small" startIcon={<AddIcon />} onClick={addRequest} sx={{ textTransform: 'none' }}>New</Button>
              <Button size="small" startIcon={<ImportIcon />} onClick={() => setImportOpen(true)} sx={{ textTransform: 'none' }}>Import</Button>
            </Box>
            <Box sx={{ flex: 1, overflow: 'auto' }}>
              {loading ? <CircularProgress size={20} sx={{ m: 2 }} /> : col.requests.map((r, i) => (
                <Box key={i} onClick={() => { setSel(i); setResponse(null); setTab(0); }}
                  sx={{ display: 'flex', alignItems: 'center', gap: 0.5, p: 0.5, borderRadius: 1, cursor: 'pointer',
                    bgcolor: i === sel ? 'action.selected' : 'transparent', '&:hover': { bgcolor: 'action.hover' } }}>
                  <Typography sx={{ fontSize: '0.6rem', fontWeight: 700, color: METHOD_COLOR[r.method] || '#666', width: 42 }}>
                    {r.method}
                  </Typography>
                  <Typography sx={{ flex: 1, fontSize: '0.78rem' }} noWrap>{r.name || r.url || 'request'}</Typography>
                  {r.security?.attacks?.length ? <Chip label={r.security.attacks.length} size="small" color="error" sx={{ height: 16, fontSize: '0.6rem' }} /> : null}
                  <IconButton size="small" onClick={(e) => { e.stopPropagation(); removeRequest(i); }}><DeleteIcon sx={{ fontSize: '0.9rem' }} /></IconButton>
                </Box>
              ))}
              {!loading && col.requests.length === 0 && (
                <Typography variant="caption" color="text.secondary" sx={{ p: 1, display: 'block' }}>
                  No requests. New, or Import a cURL / OpenAPI spec.
                </Typography>
              )}
            </Box>
            <Divider sx={{ my: 1 }} />
            <Typography variant="caption" color="text.secondary">Variables (key=value)</Typography>
            <TextField size="small" multiline minRows={2} placeholder="base_url=https://api...&#10;token={{api_token}}"
              value={varsText} onChange={(e) => setVarsText(e.target.value)} sx={{ mt: 0.5 }} />
          </Box>

          {/* Right: editor */}
          <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
            {!current ? (
              <Typography variant="body2" color="text.secondary" sx={{ m: 'auto' }}>Select or add a request.</Typography>
            ) : (
              <>
                <TextField size="small" variant="standard" value={current.name}
                  onChange={(e) => updateCurrent({ name: e.target.value })} sx={{ mb: 1 }} />
                <Box sx={{ display: 'flex', gap: 0.5, mb: 1 }}>
                  <Select size="small" value={current.method} onChange={(e) => updateCurrent({ method: e.target.value })} sx={{ width: 110 }}>
                    {METHODS.map((m) => <MenuItem key={m} value={m} sx={{ color: METHOD_COLOR[m] }}>{m}</MenuItem>)}
                  </Select>
                  <TextField size="small" placeholder="https://api.example.com/path  or  {{base_url}}/path" fullWidth
                    value={current.url} onChange={(e) => updateCurrent({ url: e.target.value })} />
                  <Button variant="contained" startIcon={sending ? <CircularProgress size={14} color="inherit" /> : <SendIcon />}
                    onClick={handleSend} disabled={sending} sx={{ textTransform: 'none' }}>Send</Button>
                </Box>

                <Tabs value={tab} onChange={(_, v) => setTab(v)} variant="scrollable" sx={{ minHeight: 34, mb: 1 }}>
                  {['Params', 'Headers', 'Body', 'Auth', 'Assert', 'Security'].map((t) => <Tab key={t} label={t} sx={{ minHeight: 34, py: 0, textTransform: 'none' }} />)}
                </Tabs>

                <Box sx={{ flex: 1, overflow: 'auto', minHeight: 120 }}>
                  {tab === 0 && <KVEditor rows={current.query} onChange={(query) => updateCurrent({ query })} />}
                  {tab === 1 && <KVEditor rows={current.headers} onChange={(headers) => updateCurrent({ headers })} />}
                  {tab === 2 && (
                    <TextField size="small" multiline minRows={6} fullWidth placeholder='{"key": "value"}'
                      value={current.body || ''} onChange={(e) => updateCurrent({ body: e.target.value })}
                      InputProps={{ sx: { fontFamily: 'monospace', fontSize: '0.8rem' } }} />
                  )}
                  {tab === 3 && (
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                      <Select size="small" value={current.auth?.type || 'inherit'}
                        onChange={(e) => updateCurrent({ auth: { ...current.auth, type: e.target.value } })} sx={{ width: 220 }}>
                        <MenuItem value="inherit">Inherit (use Test Brief / variables)</MenuItem>
                        <MenuItem value="none">No auth</MenuItem>
                        <MenuItem value="bearer">Bearer token</MenuItem>
                      </Select>
                      {current.auth?.type === 'bearer' && (
                        <TextField size="small" label="Token (or {{token}})" value={current.auth.token || ''}
                          onChange={(e) => updateCurrent({ auth: { ...current.auth!, token: e.target.value } })} />
                      )}
                    </Box>
                  )}
                  {tab === 4 && (
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                      <TextField size="small" type="number" label="Expected status" sx={{ width: 160 }}
                        value={current.assertions?.status ?? ''}
                        onChange={(e) => updateCurrent({ assertions: { ...current.assertions, status: e.target.value ? Number(e.target.value) : undefined } })} />
                      <TextField size="small" label="Response must contain (one per line)" multiline minRows={2}
                        value={(current.assertions?.contains || []).join('\n')}
                        onChange={(e) => updateCurrent({ assertions: { ...current.assertions, contains: e.target.value.split('\n').map((s) => s.trim()).filter(Boolean) } })} />
                    </Box>
                  )}
                  {tab === 5 && (
                    <Box>
                      <Typography variant="caption" color="text.secondary">
                        Generate these attack variants against this real endpoint:
                      </Typography>
                      <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                        {ATTACKS.map((a) => (
                          <FormControlLabel key={a.key}
                            control={<Checkbox size="small" checked={current.security?.attacks?.includes(a.key) || false} onChange={() => toggleAttack(a.key)} />}
                            label={<Typography variant="body2">{a.label}</Typography>} />
                        ))}
                      </Box>
                    </Box>
                  )}
                </Box>

                {response && (
                  <Box sx={{ mt: 1, p: 1, bgcolor: 'action.hover', borderRadius: 1, maxHeight: 160, overflow: 'auto' }}>
                    <Chip size="small" label={`status ${respStatus ?? '—'}`}
                      color={respStatus && respStatus < 400 ? 'success' : 'error'} sx={{ mb: 0.5 }} />
                    <Typography component="pre" sx={{ fontFamily: 'monospace', fontSize: '0.72rem', whiteSpace: 'pre-wrap', m: 0 }}>
                      {typeof (response.body ?? response.response ?? response) === 'string'
                        ? (response.body ?? response.response ?? '')
                        : JSON.stringify(response.body ?? response.response ?? response, null, 2).slice(0, 4000)}
                    </Typography>
                  </Box>
                )}
              </>
            )}

            {/* Footer actions */}
            <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end', mt: 1, pt: 1, borderTop: '1px solid', borderColor: 'divider' }}>
              {savedMsg && <Alert severity="success" sx={{ py: 0, mr: 'auto' }}>Saved.</Alert>}
              <Button startIcon={saving ? <CircularProgress size={14} /> : <SaveIcon />} onClick={handleSave}
                disabled={saving} sx={{ textTransform: 'none' }}>Save</Button>
              <Button variant="contained" color="secondary" startIcon={<RunIcon />}
                disabled={col.requests.length === 0}
                onClick={async () => { await handleSave(); setOpen(false); onRunInAgent?.(); }}
                sx={{ textTransform: 'none' }}>
                Run tests in agent
              </Button>
            </Box>
          </Box>
        </DialogContent>
      </Dialog>

      {/* Import dialog */}
      <Dialog open={importOpen} onClose={() => setImportOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Import requests</DialogTitle>
        <DialogContent>
          <Tabs value={importKind === 'curl' ? 0 : 1} onChange={(_, v) => setImportKind(v === 0 ? 'curl' : 'openapi')} sx={{ mb: 1 }}>
            <Tab label="cURL" sx={{ textTransform: 'none' }} />
            <Tab label="OpenAPI" sx={{ textTransform: 'none' }} />
          </Tabs>
          {importKind === 'curl' ? (
            <TextField fullWidth multiline minRows={4} placeholder="curl -X POST https://... -H 'Authorization: Bearer ...' -d '{...}'"
              value={curlText} onChange={(e) => setCurlText(e.target.value)}
              InputProps={{ sx: { fontFamily: 'monospace', fontSize: '0.8rem' } }} />
          ) : (
            <TextField fullWidth size="small" placeholder="https://api.example.com/openapi.json"
              value={openapiUrl} onChange={(e) => setOpenapiUrl(e.target.value)} />
          )}
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 1.5 }}>
            <Button variant="contained" onClick={handleImport} sx={{ textTransform: 'none' }}>Import</Button>
          </Box>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default ApiWorkbenchPanel;
